import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/header/Header";
import Footer from "../components/footer";
import Gotop from "../components/gotop";
import { positionAPI, applicationAPI, profileAPI } from "../services/api";
import ApplicationForm from "../components/application/ApplicationForm";
import VideoRecorder from "../components/video/VideoRecorder";
import { useAuth } from "../contexts/AuthContext";
import { getJobLocation, getSalaryDisplay, getTimezoneDisplay } from "../utils/jobDisplay";
import { Link } from "react-router-dom";
import "./JobApplication.scss";

function JobApplication() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(''); // 'uploading' or 'submitting'
  const [submitError, setSubmitError] = useState(null);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  const applicationFormRef = useRef(null);

  function statusToDisplay(status) {
    if (!status) return "Submitted";
    const s = String(status).toLowerCase().replace(/\s+/g, "-");
    const map = {
      submitted: "Submitted",
      "under-review": "Under Review",
      interview: "Under Review",
      shortlisted: "Shortlisted",
      rejected: "Rejected",
      accepted: "Hired",
      hired: "Hired",
    };
    return map[s] || "Submitted";
  }

  useEffect(() => {
    // Reset state on route change/refresh
    setSubmitSuccess(false);
    setRecordedVideo(null);
    setError(null);
    setLoading(true);
    setAlreadyApplied(false);
    setExistingApplication(null);
    fetchJobDetails();
  }, [id, user]);

  useEffect(() => {
    if (job) {
      document.title = `Careers - Talsolution, Job Application for ${job.title} at ${job.company}`;
    }
    return () => { document.title = "Talsolution"; };
  }, [job]);

  const fetchJobDetails = async () => {
    try {
      const jobRes = await positionAPI.getById(id);
      if (jobRes.data.success) {
        setJob(jobRes.data.data);
      }
      // Only check "already applied" when user is logged in
      if (user) {
        try {
          const applicationsRes = await profileAPI.getApplications();
          if (applicationsRes?.data?.success && applicationsRes.data.data?.applications) {
            const posId = id;
            const list = applicationsRes.data.data.applications;
            const pos = (a) => (a.position && typeof a.position === "object" ? a.position._id : a.positionId);
            const applied = list.find((a) => String(pos(a) || "") === String(posId));
            if (applied) {
              setAlreadyApplied(true);
              setExistingApplication({
                statusDisplay: statusToDisplay(applied.status),
                appliedDate: applied.createdAt,
              });
            }
          }
        } catch (_) {
          // Ignore: guest or API error; allow apply
        }
      }
    } catch (err) {
      setError("Failed to load job details.");
      console.error("Error fetching job:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoRecorded = (videoBlob) => {
    setRecordedVideo(videoBlob);
  };

  const handleSubmit = async () => {
    if (!applicationFormRef.current) {
      return;
    }

    // Validate form
    const isValid = applicationFormRef.current.validateForm();
    if (!isValid) {
      // Scroll to first error
      const firstError = document.querySelector('.error-message');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setSubmitting(true);
    setUploadStatus('uploading');
    setSubmitError(null); // Clear any previous errors

    try {
      // Get form data from ApplicationForm
      const formData = applicationFormRef.current.getFormData();
      
      // Add video to form data if recorded
      if (recordedVideo) {
        // Ensure the blob has the correct MIME type (video/webm) for express-fileupload
        // The blob might have codec info like 'video/webm;codecs=vp9,opus' which won't match the filter
        // Create a new blob with normalized MIME type
        const videoBlob = new Blob([recordedVideo], { type: 'video/webm' });
        formData.append("video", videoBlob, "video-introduction.webm");
        console.log("Video blob type:", videoBlob.type, "Size:", (videoBlob.size / 1024 / 1024).toFixed(2), "MB");
      }

      // Change status to submitting when API call starts
      setUploadStatus('submitting');

      const response = await applicationAPI.submit(formData);
      if (response.data.success) {
        setSubmitSuccess(true);
        window.scrollTo(0, 0);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || "Failed to submit application";
      setSubmitError(errorMessage);
      // Scroll to error message
      setTimeout(() => {
        const errorElement = document.querySelector('.submit-error-message');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setSubmitting(false);
      setUploadStatus('');
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1, padding: "100px 0" }}>
          <div className="tf-container">
            <div className="text-center">
              <p>Loading job details...</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1, padding: "100px 0" }}>
          <div className="tf-container">
            <div className="text-center">
              <h3>Job Not Found</h3>
              <p>{error || "The job you're looking for doesn't exist."}</p>
              <button className="tf-btn" onClick={() => navigate("/careers")}>
                Back to Jobs
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div style={{ flex: 1, padding: "100px 0" }}>
          <div className="tf-container">
            <div className="application-success">
              <div className="success-icon">✓</div>
              <h2>Application submitted successfully</h2>
              <p>Thank you for applying to <strong>{job.title}</strong> at <strong>{job.company}</strong>.</p>
              <p>We have received your application and will review it shortly. If your qualifications match our requirements, we'll be in touch soon.</p>
              {!user && (
                <div className="application-success-register-cta">
                  <p>Create a free Talsolution account to get more out of your job search:</p>
                  <ul>
                    <li>Track your applications in one place</li>
                    <li>Get notified about new openings that match your profile</li>
                    <li>Manage your profile and resume for faster applying</li>
                  </ul>
                  <div className="application-success-cta-buttons">
                    <Link to="/register" className="tf-btn tf-btn-primary">Register</Link>
                    <Link to="/login" className="tf-btn tf-btn-outline">Login</Link>
                  </div>
                </div>
              )}
              <Link to="/careers" className="back-button">
                <i className="icon-keyboard_arrow_left" />
                Back to Jobs
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Already applied (logged-in user): show job details only (read-only), no apply form
  if (alreadyApplied && existingApplication) {
    const statusColors = {
      Submitted: "var(--primary)",
      "Under Review": "#F59E0B",
      Shortlisted: "var(--secondary)",
      Rejected: "#EF4444",
      Hired: "var(--secondary)",
    };
    const statusColor = statusColors[existingApplication.statusDisplay] || "#6B7280";
    const appliedDateStr = existingApplication.appliedDate
      ? new Date(existingApplication.appliedDate).toLocaleDateString()
      : "—";
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Header />
        <div className="job-application-page" style={{ flex: 1 }}>
          <div className="tf-container">
            <button className="back-button" onClick={() => navigate("/applications")}>
              <i className="icon-keyboard_arrow_left"></i>
              Back to My Jobs
            </button>
              <div className="job-overview-step">
                <div className="job-overview-header">
                  <div className="job-header-content">
                    <h1 className="job-title">{job.title}</h1>
                    <p className="company-name">{job.company}</p>
                    <div className="job-meta">
                      {getJobLocation(job) && (
                        <span className="meta-item">
                          <i className="icon-map-pin"></i>
                          <span>{getJobLocation(job)}</span>
                        </span>
                      )}
                      {job.employmentType && (
                        <span className="meta-item">
                          <i className="icon-briefcase"></i>
                          <span>{job.employmentType}</span>
                        </span>
                      )}
                    </div>
                    {Array.isArray(job.workType) && job.workType.length > 0 && (
                      <div className="job-meta-badges">
                        {job.workType.map((wt) => (
                          <span
                            key={wt}
                            className={`badge badge-${String(wt).toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            {wt}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {getSalaryDisplay(job) && (
                    <div className="salary-badge">
                      <div className="salary-label">{getSalaryDisplay(job).label}</div>
                      <div className="salary-amount">{getSalaryDisplay(job).amountText}</div>
                    </div>
                  )}
                </div>
                <div className="job-overview-content">
                  {job.description && (
                    <div className="content-section">
                      <h3>About the Role</h3>
                      <div
                        className="description-content job-description-html"
                        dangerouslySetInnerHTML={{ __html: job.description }}
                      />
                    </div>
                  )}
                  {job.techStack && (
                    <div className="content-section">
                      <h3>Required Skills</h3>
                      <div className="skill-tags">
                        {job.techStack.split(',').map((skill, index) => (
                          <span key={index} className="skill-tag">{skill.trim()}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {job.timezonePreference && (
                    <div className="content-section timezone-section">
                      <p className="timezone-description">
                        Preferred working hours align with{" "}
                        <strong>{job.timezonePreference.replace(/_/g, " ")}</strong>
                        {(() => {
                          const tzDisplay = getTimezoneDisplay(job.timezonePreference);
                          if (tzDisplay?.longName || tzDisplay?.utcOffset) {
                            return (
                              <>
                                {tzDisplay.longName && <> ({tzDisplay.longName})</>} time zone
                                {tzDisplay.utcOffset && <>, which is {tzDisplay.utcOffset}</>}.
                              </>
                            );
                          }
                          return " time zone.";
                        })()}
                      </p>
                    </div>
                  )}
                  <div className="content-section">
                    <h3>Salary</h3>
                    {getSalaryDisplay(job) ? (
                      <p className="salary-range">
                        {getSalaryDisplay(job).amountText}
                        <span className="salary-currency"> {getSalaryDisplay(job).unit}</span>
                      </p>
                    ) : (
                      <p className="salary-competitive">Competitive salary based on experience</p>
                    )}
                  </div>
                </div>
                <div className="already-applied-section">
                  <div className="already-applied-box">
                    <p className="already-applied-title">You’ve already applied for this position</p>
                    <p className="already-applied-meta">
                      Applied on {appliedDateStr} · Status:{" "}
                      <span className="already-applied-status" style={{ backgroundColor: statusColor }}>
                        {existingApplication.statusDisplay}
                      </span>
                    </p>
                    <p className="already-applied-hint">This page is for viewing job details only. You cannot submit another application.</p>
                    <button type="button" className="btn-back-to-my-jobs" onClick={() => navigate("/applications")}>
                      Back to My Jobs
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
          <Gotop />
        </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      <div className="job-application-page" style={{ flex: 1 }}>
        <div className="tf-container">
          {/* Back Button - go to job detail page */}
          <button className="back-button" onClick={() => navigate(`/careers/${id}`)}>
            <i className="icon-keyboard_arrow_left"></i>
            Back to job
          </button>

          <div className="application-form-step">
              <div className="application-header">
                <div className="header-content">
                  <h2 className="form-title">Apply for Position</h2>
                  <p className="form-subtitle">{job.title} at {job.company}</p>
                </div>
                <div className="progress-indicator">
                  <div className="progress-step active">
                    <span className="step-number">1</span>
                    <span className="step-label">Personal Info</span>
                  </div>
                  <div className="progress-line"></div>
                  <div className="progress-step active">
                    <span className="step-number">2</span>
                    <span className="step-label">Video Intro</span>
                  </div>
                </div>
              </div>

              <div className="application-form-wrapper">
                <div className="form-section">
                  <h3 className="section-title">
                    <span className="section-icon">👤</span>
                    Personal Information
                  </h3>
                  <ApplicationForm 
                    ref={applicationFormRef}
                    job={job} 
                    recordedVideo={recordedVideo}
                  />
                </div>

                <div className="form-section video-section">
                  <h3 className="section-title">
                    <span className="section-icon">🎥</span>
                    Video Introduction
                  </h3>
                  <VideoRecorder 
                    key={`video-recorder-${id}`}
                    onVideoRecorded={handleVideoRecorded}
                    onCancel={() => setRecordedVideo(null)}
                  />
                </div>

                <div className="form-actions">
                  {submitError && (
                    <div className="submit-error-message">
                      <span className="error-icon">⚠️</span>
                      <span>{submitError}</span>
                      <button 
                        className="close-error" 
                        onClick={() => setSubmitError(null)}
                        aria-label="Close error"
                      >
                        ×
                      </button>
                    </div>
                  )}
                  <button 
                    type="button"
                    onClick={handleSubmit}
                    className="submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="app-spinner app-spinner--sm" />
                        {uploadStatus === 'uploading' ? 'Uploading...' : uploadStatus === 'submitting' ? 'Submitting...' : 'Processing...'}
                      </>
                    ) : (
                      <>
                        <i className="icon-paper-plane-o"></i>
                        Submit Application
                      </>
                    )}
                  </button>
                  {!recordedVideo && (
                    <p className="video-optional-note">
                      <span className="note-icon">ℹ️</span>
                      Video introduction is optional. You can submit without it or record one to help our team get to know you better.
                    </p>
                  )}
                </div>
              </div>
            </div>
        </div>
      </div>

      <Footer />
      <Gotop />
    </div>
  );
}

export default JobApplication;
